import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import {
  emailLogService,
  type EmailLogFilters,
  type RegistrarEnvioManualInput,
} from '@/services/emailLogService'
import { toast } from 'sonner'
import { cuentaCorrienteKeys } from '@/hooks/useCuentaCorriente'

export const emailLogKeys = {
  all: ['email-log'] as const,
  lists: () => [...emailLogKeys.all, 'list'] as const,
  list: (filtros?: EmailLogFilters) => [...emailLogKeys.lists(), filtros] as const,
  details: () => [...emailLogKeys.all, 'detail'] as const,
  detail: (id: string) => [...emailLogKeys.details(), id] as const,
  suppressed: () => [...emailLogKeys.all, 'suppressed'] as const,
  facetas: () => [...emailLogKeys.all, 'facetas'] as const,
}

export function useEmailLogs(filtros: EmailLogFilters = {}, enabled = true) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: emailLogKeys.list(filtros),
    queryFn: () => emailLogService.listar(supabase!, filtros),
    staleTime: 30000,
    enabled: !!supabase && !supabaseLoading && enabled,
  })
}

/**
 * Opciones de los filtros del historial: los tipos y estados que la organización tiene
 * registrados. Vive aparte del listado porque no depende de la página ni de los filtros activos.
 * Con `enviadoPor` cuenta solo los envíos de ese remitente, para que las opciones tengan el mismo
 * alcance que una lista filtrada por remitente.
 */
export function useEmailLogFacetas(organizacionId?: string, enviadoPor?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: [...emailLogKeys.facetas(), organizacionId, enviadoPor ?? null],
    queryFn: () => emailLogService.facetas(supabase!, organizacionId!, enviadoPor),
    staleTime: 5 * 60 * 1000,
    // Los envíos los registran las Netlify Functions, que no pueden invalidar este caché. Sin
    // esto, un tipo de email estrenado hace un rato no aparece entre las opciones hasta que
    // vence el staleTime.
    refetchOnMount: 'always',
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

export function useEmailLog(id: string, organizacionId?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: emailLogKeys.detail(id),
    queryFn: () => emailLogService.obtenerPorId(supabase!, id, organizacionId!),
    staleTime: 30000,
    enabled: !!supabase && !supabaseLoading && !!id && !!organizacionId,
  })
}

export function useEmailSuprimidos(organizacionId?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: [...emailLogKeys.suppressed(), organizacionId],
    queryFn: () => emailLogService.listarSuprimidos(supabase!, organizacionId),
    staleTime: 60000,
    enabled: !!supabase && !supabaseLoading,
  })
}

export function useEliminarSupresion() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (id: string) => {
      if (!supabase) throw new Error('Supabase no disponible')
      return emailLogService.eliminarSupresion(supabase, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailLogKeys.suppressed() })
      toast.success('Email removido de la lista de supresion')
    },
    onError: (error) => {
      console.error('Error eliminando supresion:', error)
      toast.error('Error al eliminar supresion')
    },
  })
}

function useUltimosEnviosPorTipo(clienteIds: string[], tipos: readonly string[], organizacionId?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: [
      ...emailLogKeys.all,
      'ultimos-envios',
      organizacionId,
      [...tipos].sort().join(','),
      clienteIds.length,
      [...clienteIds].sort().join(','),
    ],
    queryFn: () => emailLogService.ultimoEnvioPorClienteIds(supabase!, clienteIds, [...tipos], organizacionId),
    staleTime: 60000,
    enabled: !!supabase && !supabaseLoading && clienteIds.length > 0,
  })
}

export function useUltimosEnviosEstadoCuenta(clienteIds: string[], organizacionId?: string) {
  return useUltimosEnviosPorTipo(clienteIds, ['estado_cuenta'], organizacionId)
}

export function useRegistrarEnvioManual() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (input: RegistrarEnvioManualInput) => {
      if (!supabase) throw new Error('Supabase no disponible')
      return emailLogService.registrarEnvioManual(supabase, input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailLogKeys.lists() })
      queryClient.invalidateQueries({ queryKey: [...emailLogKeys.all, 'ultimos-envios'] })
      queryClient.invalidateQueries({ queryKey: emailLogKeys.facetas() })
      // ORR-109: vista_ranking_morosos lee ultima_notificacion de email_log, y la consulta
      // cuentaCorrienteService bajo cuentaCorrienteKeys (resumen de la ficha del cliente).
      queryClient.invalidateQueries({ queryKey: cuentaCorrienteKeys.all })
      toast.success('Envío registrado correctamente')
    },
    onError: (error) => {
      console.error('Error registrando envío manual:', error)
      toast.error('No se pudo registrar el envío')
    },
  })
}

export function useAgregarSupresionManual() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: ({ email, organizacionId }: { email: string; organizacionId?: string }) => {
      if (!supabase) throw new Error('Supabase no disponible')
      return emailLogService.agregarSupresionManual(supabase, email, organizacionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailLogKeys.suppressed() })
      toast.success('Email agregado a la lista de supresion')
    },
    onError: (error) => {
      console.error('Error agregando supresion:', error)
      toast.error('Error al agregar supresion')
    },
  })
}
